import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import {ArrowLeft, MapPin, Calendar, Euro, Building2, CheckCircle2, Clock, Send, ShieldCheck, Waves, FolderCheck, Upload, AlertTriangle, Home, Zap, GraduationCap, TrendingUp, Save, User as UserIcon} from 'lucide-react';
import toast from "react-hot-toast";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import CandidateSignupModal from "@/components/CandidateSignupModal";
import Breadcrumb, { type BreadcrumbItem } from "@/components/Breadcrumb";
import { jobs, CONTRACT_LABELS } from "@/data/jobs";
import { useApplications } from "@/hooks/useApplications";
import { useFilledJobIds } from "@/hooks/useFilledJobIds";
import { useAuth } from "@/hooks/useAuth";
import { useProDossier } from "@/hooks/useProDossier";
import { lumi } from "@/lib/lumi";
import { uploadOneFile } from "@/lib/upload";
import { buildJobPath, extractIdFromSlug } from "@/lib/jobUrl";
import { getPrerenderJob } from "@/lib/prerenderJobs";
import { trackJobView, trackJobApplicationStart, trackJobApplicationSubmit } from "@/lib/analytics";
import type { Job } from "@/data/jobs";

// Types pour le dossier professionnel auto-entrepreneur
type DocKey =
  | "cartePro"
  | "assurancePro"
  | "diplome"
  | "caepmns"
  | "pse"
  | "recyclagePse"
  | "recyclageFcBnssa";

interface StoredProDossier {
  category: "BNSSA" | "MNS";
  diplomeType?: string;
  files: Partial<Record<DocKey, string>>; // nom de fichier uniquement (mock)
  updatedAt: string;
}

const DOSSIER_STORAGE_KEY = "proDossier";

export default function JobDetail() {
  const params = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, profile } = useAuth();
  const { createApplication } = useApplications();
  const { fetchMine: fetchMyDossier, upsertDossier } = useProDossier();
  // Missions AE/Intérim « pourvues » (candidat retenu) → accès bloqué.
  const { isFilled: isJobFilled } = useFilledJobIds();

  // Résolution de l'ID de l'annonce depuis l'URL :
  //  - nouvelles routes SEO : /bnssa/offres-emploi/:jobSlug ou /maitre-nageur-sauveteur/offres-emploi/:jobSlug
  //    où :jobSlug = <title>-<ville>-<dep>-<id>
  //  - anciennes routes : /offre/:id et /offres/:id (compat) → on redirige ensuite vers la nouvelle URL canonique
  const id = useMemo(() => {
    if (params.id) return params.id;
    return extractIdFromSlug(params.jobSlug);
  }, [params.id, params.jobSlug]);

  // Statut du dossier auto-entrepreneur (pour bloquer si non validé)
  const [dossierStatus, setDossierStatus] = useState<'pending' | 'approved' | 'rejected' | 'none'>('none');
  const [dossierStatusLoading, setDossierStatusLoading] = useState(false);
  // Indique si un dossier existe RÉELLEMENT en base pour cet utilisateur connecté
  // (ne se base PAS sur localStorage, qui peut être pollué par d'autres comptes
  // ayant utilisé le même navigateur).
  const [dossierFromDb, setDossierFromDb] = useState(false);

  // Si le candidat est déjà connecté, on récupère son nom et son email
  // pour pré-remplir le formulaire et éviter qu'il ne les ressaisisse.
  const prefillFullName = profile?.fullName || user?.userName || "";
  const prefillEmail = user?.email || "";
  const isLoggedIn = Boolean(user?.userId);

  // Static job lookup, fallback to recruiter_jobs in MongoDB.
  const staticJob = useMemo(() => jobs.find((j) => j.id === id), [id]);
  // Pré-remplissage SYNCHRONE depuis le cache de prerender (build/SSR) afin que
  // <Seo> écrive le bon <title>/og:title dès le premier rendu serveur, sans
  // attendre le chargement async de recruiter_jobs (invisible aux crawlers).
  // Côté navigateur le cache est vide → comportement runtime inchangé.
  const prerenderJob = useMemo(() => getPrerenderJob(id), [id]);
  const [dbJob, setDbJob] = useState<Job | null>(prerenderJob);
  const [jobLoading, setJobLoading] = useState(false);

  useEffect(() => {
    if (!id || staticJob) return;
    let cancelled = false;
    setJobLoading(true);

    // Cherche l'offre dans recruiter_jobs : on liste TOUTES les offres puis on
    // matche côté client par _id (string). Le filtre `_id` MongoDB attend un
    // ObjectId, ce qui ne fonctionne pas via le SDK pour les IDs string.
    lumi.entities.recruiter_jobs
      .list({})
      .then(async ({ list }) => {
        if (cancelled) return;
        const all = list as Array<Record<string, unknown>>;
        const rj = all.find((j) => String(j._id) === String(id));
        if (!rj) return;
        const r = rj as {
          _id: string; title: string; employer: string; location: string;
          department: string; category: "BNSSA" | "MNS";
          contractType: Job["contractType"]; salary?: string; hourlyRate?: string;
          description: string; requirements?: string[]; benefits?: string[];
          publishedAt?: string; createdAt?: string; deadline?: string; startDate?: string; endDate?: string;
          creator?: string; logoUrl?: string;
          missionDate?: string; missionStartTime?: string; missionEndTime?: string;
          missionHours?: number; missionPrice?: number; activities?: string[];
        };

        // Jointure logo recruteur : on lit d'abord `logoUrl` directement sur
        // l'offre (s'il y a déjà été dénormalisé), sinon on va chercher le
        // `logoUrl` du `company_profiles` correspondant au `creator` de l'offre.
        let recruiterLogo: string | undefined = r.logoUrl;
        if (!recruiterLogo && r.creator) {
          try {
            const profilesRes = await lumi.entities.company_profiles.list({
              filter: { creator: r.creator },
              limit: 1,
            });
            const cp = (profilesRes.list as Array<Record<string, unknown>>)?.[0];
            const cpLogo = cp?.logoUrl as string | undefined;
            if (cpLogo) recruiterLogo = cpLogo;
          } catch (e) {
            console.warn("company_profiles join (logo) failed:", e);
          }
        }

        if (cancelled) return;
        setDbJob({
          id: String(r._id),
          title: r.title,
          employer: r.employer,
          location: r.location,
          department: r.department,
          category: r.category,
          contractType: r.contractType,
          salary: r.salary,
          hourlyRate: r.hourlyRate,
          description: r.description,
          requirements: r.requirements || [],
          benefits: r.benefits || [],
          publishedAt: (r.publishedAt || r.createdAt || "").slice(0, 10),
          deadline: r.deadline,
          startDate: r.startDate,
          endDate: r.endDate,
          urgent: (rj as Record<string, unknown>).urgent as boolean | undefined,
          logementPossible: (rj as Record<string, unknown>).logementPossible as boolean | undefined,
          horairesFixes: (rj as Record<string, unknown>).horairesFixes as boolean | undefined,
          coursParticuliers: (rj as Record<string, unknown>).coursParticuliers as boolean | undefined,
          possibiliteCDI: (rj as Record<string, unknown>).possibiliteCDI as boolean | undefined,
          logo: recruiterLogo,
          missionDate: r.missionDate,
          missionStartTime: r.missionStartTime,
          missionEndTime: r.missionEndTime,
          missionHours: r.missionHours,
          missionPrice: r.missionPrice,
          activities: r.activities,
        });
      })
      .catch((err) => console.error("recruiter_jobs lookup failed", err))
      .finally(() => {
        if (!cancelled) setJobLoading(false);
      });
    return () => { cancelled = true; };
  }, [id, staticJob]);

  const job = staticJob || dbJob;

  // Calcule l'URL canonique SEO-friendly de cette annonce une fois résolue.
  const canonicalPath = useMemo(() => (job ? buildJobPath(job) : null), [job]);
  // Domaine canonique aligné sur le domaine réellement servi (www.bnssa-mns.fr),
  // pour éviter que Facebook/Google traitent www et non-www comme deux pages.
  const canonicalUrl = canonicalPath ? `https://www.bnssa-mns.fr${canonicalPath}` : undefined;

  // Redirection automatique :
  //  - depuis l'ancien /offre/:id ou /offres/:id vers la nouvelle URL canonique SEO
  //  - depuis une URL silo dont le slug ne correspond plus exactement (titre/ville/dép modifiés) vers le bon slug
  useEffect(() => {
    if (!job || !canonicalPath) return;
    if (pathname !== canonicalPath) {
      navigate(canonicalPath, { replace: true });
    }
  }, [job, canonicalPath, pathname, navigate]);

  // ── Sauvegarde automatique du brouillon de candidature ───────────────────
  // Clé localStorage par offre + utilisateur (ou "guest") afin d'éviter qu'un
  // brouillon ne se mélange entre comptes ou entre offres différentes.
  const draftStorageKey = useMemo(
    () => `applicationDraft:${id || "unknown"}:${user?.userId || "guest"}`,
    [id, user?.userId]
  );

  // Charge un éventuel brouillon existant au démarrage.
  const initialDraft = useMemo(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = localStorage.getItem(draftStorageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as {
        fullName?: string;
        email?: string;
        phone?: string;
        message?: string;
        cvFileName?: string;
        proposedRate?: string;
        savedAt?: string;
      };
      return parsed;
    } catch {
      return null;
    }
  }, [draftStorageKey]);

  const [form, setForm] = useState({
    fullName: initialDraft?.fullName || prefillFullName,
    email: initialDraft?.email || prefillEmail,
    phone: initialDraft?.phone || profile?.phone || "",
    message: initialDraft?.message || "",
    cvFileName: initialDraft?.cvFileName || "",
    proposedRate: initialDraft?.proposedRate || "",
  });

  // Indicateur de sauvegarde du brouillon (affiché brièvement après chaque save)
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(
    initialDraft?.savedAt || null
  );
  const [draftJustSaved, setDraftJustSaved] = useState(false);

  // Si l'utilisateur se connecte/déconnecte pendant la session, on resynchronise.
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      fullName: prev.fullName || prefillFullName,
      email: prev.email || prefillEmail,
      phone: prev.phone || profile?.phone || "",
    }));
  }, [prefillFullName, prefillEmail, profile?.phone]);

  const [submitted, setSubmitted] = useState(false);

  // Sauvegarde automatique du brouillon (debounced 600ms) à chaque changement
  // significatif. On ne stocke que les champs texte (pas de File brut).
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (submitted) return; // Ne pas écraser après envoi
    // On évite de sauvegarder un brouillon strictement vide.
    const hasContent =
      form.fullName.trim() ||
      form.email.trim() ||
      form.phone.trim() ||
      form.message.trim() ||
      form.proposedRate.trim();
    if (!hasContent) return;

    const timer = setTimeout(() => {
      try {
        const savedAt = new Date().toISOString();
        localStorage.setItem(
          draftStorageKey,
          JSON.stringify({
            fullName: form.fullName,
            email: form.email,
            phone: form.phone,
            message: form.message,
            cvFileName: form.cvFileName,
            proposedRate: form.proposedRate,
            savedAt,
          })
        );
        setDraftSavedAt(savedAt);
        setDraftJustSaved(true);
        // Reset du flash visuel après 1.6s
        setTimeout(() => setDraftJustSaved(false), 1600);
      } catch {
        // ignore (quota, mode privé Safari, etc.)
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [form.fullName, form.email, form.phone, form.message, form.cvFileName, form.proposedRate, draftStorageKey, submitted]);

  // Helper : nettoie le brouillon (appelé après un envoi réussi).
  const clearDraft = () => {
    try {
      localStorage.removeItem(draftStorageKey);
    } catch {
      // ignore
    }
    setDraftSavedAt(null);
  };

  // Fichier CV brut (avant upload), URL OSS après upload.
  const [cvFile, setCvFile] = useState<File | null>(null);

  // Dossier professionnel (uniquement auto-entrepreneur) — stocké en base Lumi.
  const [storedDossier, setStoredDossier] = useState<StoredProDossier | null>(null);
  // Fichiers bruts (avant upload) — keyés par DocKey.
  const [docRawFiles] = useState<Partial<Record<DocKey, File>>>({});
  const [submitting, setSubmitting] = useState(false);

  // Modale d'inscription rapide (déclenchée si le candidat n'est pas connecté)
  const [signupOpen, setSignupOpen] = useState(false);

  // Charge le dossier depuis la base de données (utilisateur connecté), fallback localStorage (invités).
  // Pour les offres auto-entrepreneur, on récupère aussi le statut de validation.
  useEffect(() => {
    let cancelled = false;

    const loadFromLocalStorage = () => {
      try {
        const raw = localStorage.getItem(DOSSIER_STORAGE_KEY);
        if (raw && !cancelled) {
          const parsed: StoredProDossier = JSON.parse(raw);
          setStoredDossier(parsed);
        }
      } catch {
        // ignore
      }
    };

    if (user?.userId) {
      // Utilisateur connecté → SEULE la base fait foi (jamais le localStorage,
      // qui peut contenir un dossier déposé par un autre compte sur ce navigateur).
      setDossierStatusLoading(true);
      setDossierFromDb(false);
      fetchMyDossier(user.userId, user.email)
        .then((dossier) => {
          if (cancelled) return;
          if (dossier) {
            setStoredDossier({
              category: dossier.category,
              diplomeType: dossier.diplomeType,
              files: dossier.files || {},
              updatedAt: dossier.updatedAt,
            });
            setDossierFromDb(true);
            // Statut de validation du dossier
            const status = (dossier as unknown as Record<string, unknown>).validationStatus as string | undefined;
            setDossierStatus(
              status === 'approved' ? 'approved'
              : status === 'rejected' ? 'rejected'
              : status === 'pending' ? 'pending'
              : 'none'
            );
          } else {
            // Pas de dossier en base → l'utilisateur connecté DOIT téléverser ses pièces.
            // On vide tout résidu localStorage qui aurait pu être laissé par un autre
            // compte ou par un ancien parcours invité, pour ne pas faire croire à tort
            // que le dossier est déjà au complet.
            setStoredDossier(null);
            setDossierFromDb(false);
            setDossierStatus('none');
          }
        })
        .catch(() => {
          setStoredDossier(null);
          setDossierFromDb(false);
          setDossierStatus('none');
        })
        .finally(() => {
          if (!cancelled) setDossierStatusLoading(false);
        });
    } else {
      // Invité non connecté → localStorage en pré-remplissage uniquement
      // (les pièces seront re-vérifiées lors de la création du compte + dossier).
      loadFromLocalStorage();
      setDossierFromDb(false);
      setDossierStatus('none');
    }

    return () => {
      cancelled = true;
    };
  }, [user?.userId, user?.email, fetchMyDossier]);

  // GA4 - vue d'une offre d'emploi (une fois par job affiché)
  // DOIT être avant tout return anticipé pour respecter les Rules of Hooks
  useEffect(() => {
    if (!job?.id) return;
    trackJobView({
      jobId: String(job.id),
      category: job.category,
      contractType: job.contractType,
      department: job.department,
      isAutoEntrepreneur: job.contractType === "auto-entrepreneur",
    });
  }, [job?.id, job?.category, job?.contractType, job?.department]);

  if (!job) {
    return (
      <Layout>
        <Seo title={jobLoading ? "Chargement…" : "Offre introuvable"} description="Cette offre n'existe pas ou a été retirée." />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold mb-4">{jobLoading ? "Chargement…" : "Offre introuvable"}</h1>
          {!jobLoading && (
            <>
              <p className="text-gray-600 mb-6">L'offre demandée n'existe pas ou a été retirée.</p>
              <button
                onClick={() => navigate("/offres")}
                className="bg-blue-600 text-white px-5 py-2.5 rounded-lg font-semibold"
              >
                Retour aux offres
              </button>
            </>
          )}
        </div>
      </Layout>
    );
  }

  const isBNSSA = job.category === "BNSSA";
  const isAutoEntrepreneur = job.contractType === "auto-entrepreneur";
  const isInterim = job.contractType === "interim";
  const isMissionContract = isAutoEntrepreneur || isInterim;

  // Mission AE/Intérim pourvue : un candidat a déjà été retenu.
  // → On affiche désormais l'annonce COMPLÈTE (description, mission, activités…)
  //   avec un tampon « Mission pourvue » bien visible dans le hero, et on
  //   DÉSACTIVE uniquement le formulaire / bouton de candidature pour empêcher
  //   toute nouvelle postulation (sans bloquer la consultation de l'offre).
  const missionFilled = isMissionContract && isJobFilled(job.id);
  const hasMissionDetails = isMissionContract && Boolean(job.missionDate || job.missionStartTime);
  const accentBg = isBNSSA ? "bg-red-600" : "bg-blue-600";
  const accentBgLight = isBNSSA ? "bg-red-50" : "bg-blue-50";
  const accentText = isBNSSA ? "text-red-700" : "text-blue-700";
  const accentBorder = isBNSSA ? "border-red-200" : "border-blue-200";

  // Image Open Graph (partage réseaux sociaux) spécifique à cette annonce.
  // Priorité : logo du recruteur s'il est présent, sinon image d'univers
  // brandée bnssa-mns.fr (BNSSA rouge / MNS bleu) — visuel sombre #1e293b
  // avec wordmark et accroche « On cherche un BNSSA / MNS ».
  // IMPORTANT : ce sont de vraies images PNG hébergées (et non un SVG), car
  // LinkedIn/Facebook/Twitter n'acceptent pas le SVG comme og:image.
  const OG_IMAGE_BNSSA =
    "https://static.lumi.new/c1/c1d577b130fda8f44555d8f1a60b2ecf.png";
  const OG_IMAGE_MNS =
    "https://static.lumi.new/9d/9d9d526ffde15f9dbf7a9fb55396b65e.png";
  const ogImage = job.logo || (isBNSSA ? OG_IMAGE_BNSSA : OG_IMAGE_MNS);

  // Format lisible de la date de mission
  const formatMissionDateLong = (iso?: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  };

  const aeDossierPending = isAutoEntrepreneur && dossierStatus === 'pending';
  const aeDossierApproved = isAutoEntrepreneur && dossierStatus === 'approved';
  // Un candidat connecté avec dossier en attente OU sans dossier OU rejeté
  // → il ne peut pas encore postuler à une offre AE.
  // Seul un dossier validé par l'admin permet la candidature AE.
  const showAEBlockedMessage = isAutoEntrepreneur && Boolean(user?.userId) && !aeDossierApproved;

  // Pour les offres auto-entrepreneur, on simplifie le flux d'inscription :
  //  - Avant inscription (candidat non connecté) : on demande Nom, Email, Téléphone, Lettre de motivation.
  //    Le champ "Tarif horaire proposé" est GRISÉ (disponible après validation du dossier par l'admin).
  //    Aucune pièce du dossier professionnel ni CV n'est demandé à ce stade.
  //  - Après inscription : le candidat est redirigé vers son dashboard pour téléverser ses pièces.
  //    Tant que le dossier n'est pas validé par l'admin, le tarif horaire reste grisé et
  //    la candidature ne peut pas être envoyée.
  const aePreSignup = isAutoEntrepreneur && !isLoggedIn;
  // Le tarif horaire est saisissable uniquement si le candidat est connecté ET que son
  // dossier a été VALIDÉ par l'admin.
  const aeRateLocked = isAutoEntrepreneur && !aeDossierApproved;

  // Dossier déjà enregistré pour la même catégorie ?
  // CRITIQUE : pour un utilisateur connecté, on exige que le dossier existe
  // RÉELLEMENT en base (dossierFromDb). Sans cela, on ne fait JAMAIS confiance
  // au localStorage : c'est exactement le bug rapporté (un candidat ayant déjà
  // postulé en CDI pouvait postuler à une offre AE sans documents).
  // Pour un invité, on autorise le localStorage uniquement comme pré-remplissage,
  // mais l'inscription ré-uploadera les pièces de toute façon.
  const dossierAlreadyOnFile =
    isAutoEntrepreneur &&
    storedDossier?.category === job.category &&
    (user?.userId ? dossierFromDb : true);

  // Soumission effective de la candidature (Mongo + dossier + email).
  // Extraite pour pouvoir l'appeler aussi depuis la modale d'inscription une
  // fois que le candidat a créé son compte.
  const submitApplication = async (
    creatorId: string,
    candidateUserId?: string
  ): Promise<boolean> => {
    if (!job) return false;

    let dossierToPersist: StoredProDossier | null = null;
    let rateNum: number | undefined;

    if (isAutoEntrepreneur) {
      rateNum = parseFloat(form.proposedRate.replace(",", "."));
      // Le tarif horaire n'est demandé que si le dossier est VALIDÉ par l'admin.
      // Sinon (pré-inscription, dossier en attente, rejeté ou inexistant), on n'envoie
      // pas de tarif — la candidature elle-même est d'ailleurs bloquée plus haut.
      if (aeDossierApproved) {
        if (!form.proposedRate || isNaN(rateNum) || rateNum <= 0) {
          toast.error("Merci d'indiquer votre tarif horaire proposé (€/h).");
          return false;
        }
      } else {
        rateNum = undefined;
      }

      // Plus de validation des pièces du dossier au moment de la candidature :
      // l'upload des documents pro se fait depuis le dashboard après la candidature.
      if (storedDossier && dossierAlreadyOnFile) {
        dossierToPersist = storedDossier;
      }
    }

    setSubmitting(true);
    try {
      // 1) Upload du CV — uniquement pour les offres standards.
      //    Pour les offres auto-entrepreneur : pas de CV (ni à l'inscription, ni dans le dossier).
      let cvUrl: string | undefined;
      if (!isAutoEntrepreneur && cvFile) {
        const url = await uploadOneFile(cvFile);
        if (url) cvUrl = url;
        else toast.error("L'upload du CV a échoué — la candidature continue sans CV.");
      }

      // 2) Upload des pièces du dossier auto-entrepreneur (si nouveau dossier).
      if (dossierToPersist && !dossierAlreadyOnFile) {
        const filesUrlMap: Partial<Record<DocKey, string>> = {};
        const entries = Object.entries(docRawFiles) as [DocKey, File | undefined][];
        const uploads = await Promise.all(
          entries.map(async ([key, f]) => {
            if (!f) return null;
            const url = await uploadOneFile(f);
            return url ? ([key, url] as const) : null;
          })
        );
        uploads.forEach((res) => {
          if (res) filesUrlMap[res[0]] = res[1];
        });
        // On remplace les noms de fichiers par les URLs OSS dans le snapshot dossier
        dossierToPersist = {
          ...dossierToPersist,
          files: { ...dossierToPersist.files, ...filesUrlMap },
        };
      }

      await createApplication(
        {
          jobId: job.id,
          jobTitle: job.title,
          jobEmployer: job.employer,
          jobCategory: job.category,
          contractType: job.contractType,
          fullName: form.fullName,
          email: form.email,
          phone: form.phone || undefined,
          // Lettre de motivation : transmise uniquement pour les offres standards.
          // Pour les auto-entrepreneurs : pas de lettre de motivation (ni CV).
          message: !isAutoEntrepreneur ? form.message : undefined,
          // Le nom + URL du CV sont transmis uniquement pour les offres standards
          // (les auto-entrepreneurs n'ont pas de CV).
          cvFileName: !isAutoEntrepreneur && cvFile ? form.cvFileName || undefined : undefined,
          cvUrl,
          proposedRate: rateNum,
          dossier: dossierToPersist || undefined,
          candidateId: candidateUserId,
        },
        creatorId
      );

      if (dossierToPersist && !dossierAlreadyOnFile) {
        if (candidateUserId) {
          try {
            await upsertDossier(
              {
                category: dossierToPersist.category,
                diplomeType: dossierToPersist.diplomeType,
                files: dossierToPersist.files,
                fullName: form.fullName,
                email: form.email,
                phone: form.phone || undefined,
              },
              candidateUserId
            );
          } catch (dossierErr) {
            console.warn("pro_dossiers upsert failed (non-blocking):", dossierErr);
          }
        }
        try {
          localStorage.setItem(DOSSIER_STORAGE_KEY, JSON.stringify(dossierToPersist));
        } catch {
          // ignore
        }
        setStoredDossier(dossierToPersist);
      }

      setSubmitted(true);
      trackJobApplicationSubmit({
        jobId: String(job.id),
        category: job.category,
        contractType: job.contractType,
        isAutoEntrepreneur,
      });
      // On nettoie le brouillon de candidature dès qu'elle est envoyée avec succès
      clearDraft();
      toast.success(
        isAutoEntrepreneur && rateNum
          ? `Candidature envoyée avec votre tarif de ${form.proposedRate} €/h !`
          : "Candidature envoyée avec succès !"
      );
      return true;
    } catch (err) {
      console.error("createApplication failed:", err);
      toast.error("Échec de l'envoi de la candidature. Merci de réessayer.");
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    trackJobApplicationStart({
      jobId: String(job.id),
      category: job.category,
      contractType: job.contractType,
      isAutoEntrepreneur,
    });
    e.preventDefault();

    // Validation des champs de base.
    //  - AE pré-inscription : Nom + Email + Téléphone + Lettre de motivation (sans CV)
    //  - AE post-inscription (dossier validé) : Nom + Email + tarif horaire
    //  - Standard : Nom + Email + Lettre de motivation
    let baseValid = false;
    if (isAutoEntrepreneur && aePreSignup) {
      // Pour les AE en pré-inscription : pas de CV, pas de lettre de motivation.
      // Seules les coordonnées sont nécessaires pour créer le compte.
      baseValid = Boolean(form.fullName && form.email && form.phone);
      if (!baseValid) {
        toast.error("Merci de remplir tous les champs obligatoires (téléphone inclus).");
        return;
      }
    } else if (isAutoEntrepreneur) {
      baseValid = Boolean(form.fullName && form.email);
    } else {
      baseValid = Boolean(form.fullName && form.email && form.message);
    }
    if (!baseValid) {
      toast.error("Merci de remplir tous les champs obligatoires.");
      return;
    }

    // Si auto-entrepreneur connecté avec dossier validé, on valide le tarif horaire.
    if (isAutoEntrepreneur && aeDossierApproved) {
      const rateNum = parseFloat(form.proposedRate.replace(",", "."));
      if (!form.proposedRate || isNaN(rateNum) || rateNum <= 0) {
        toast.error("Merci d'indiquer votre tarif horaire proposé (€/h).");
        return;
      }
    }

    // Candidat connecté mais dossier AE non validé → on bloque l'envoi.
    if (isAutoEntrepreneur && user?.userId && !aeDossierApproved) {
      toast.error("Votre dossier auto-entrepreneur doit être validé par notre équipe avant de pouvoir postuler.");
      setTimeout(() => navigate("/dashboard?tab=dossier"), 800);
      return;
    }

    // Candidat déjà connecté → envoi direct (offres standards ou AE avec dossier validé)
    if (user?.userId) {
      await submitApplication(user.userId, user.userId);
      return;
    }

    // Candidat non connecté → on ouvre la modale d'inscription/vérification
    setSignupOpen(true);
  };

  return (
    <Layout>
      <Seo
        title={(() => {
          // Title SEO : on remet le H1 (titre de l'annonce) en tête, suivi de la
          // localisation Ville (code dép.) pour conserver l'avantage SEO local,
          // puis l'employeur.
          // Extraction du code numérique depuis "Rhône (69)" → "69".
          const dep = (job.department || "").trim();
          const codeMatch = dep.match(/\((\d+[A-Z]?)\)/);
          const depCode = codeMatch ? codeMatch[1] : dep;
          const localisation = depCode
            ? `${job.location} (${depCode})`
            : job.location;
          return `${job.title} - ${localisation} | ${job.employer}`;
        })()}
        description={`${job.title} chez ${job.employer} à ${job.location} (${job.department}). Contrat ${CONTRACT_LABELS[job.contractType]}${!isAutoEntrepreneur && job.salary ? ` — ${job.salary}` : ""}. Postulez en ligne sur bnssa-mns.fr.`}
        canonical={canonicalUrl}
        ogImage={ogImage}
        ogType="article"
        jsonLd={(() => {
          // ─── Construction du JSON-LD JobPosting optimisé Google for Jobs ───
          // Description : Google recommande du HTML structuré (paragraphes, listes).
          // On garantit a minima un paragraphe enveloppant si la description est en texte brut.
          const rawDesc = job.description || `Poste de ${job.category === "BNSSA" ? "surveillant aquatique BNSSA" : "Maître Nageur Sauveteur"} chez ${job.employer} à ${job.location}.`;
          const htmlDesc = /<\/?[a-z][\s\S]*>/i.test(rawDesc)
            ? rawDesc
            : `<p>${rawDesc.replace(/\n\s*\n/g, "</p><p>").replace(/\n/g, "<br/>")}</p>`;

          // validThrough : Google attend une date ISO complète (avec heure)
          // On force 23:59:59 sur la dernière journée pour rester précis.
          const computeValidThrough = (): string | undefined => {
            if (job.deadline) return `${job.deadline}T23:59:59+01:00`;
            if (isMissionContract && job.missionDate) {
              const endTime = job.missionEndTime || "23:59";
              return `${job.missionDate}T${endTime}:00+01:00`;
            }
            return undefined;
          };
          const validThroughIso = computeValidThrough();

          // workHours : depuis missionStartTime/missionEndTime pour AE/Intérim, sinon défauts.
          const computeWorkHours = (): string => {
            if (isMissionContract && job.missionStartTime && job.missionEndTime) {
              return `${job.missionStartTime}–${job.missionEndTime}${
                typeof job.missionHours === "number" ? ` (${job.missionHours} h)` : ""
              }`;
            }
            if (job.contractType === "temps-partiel-CDI" || job.contractType === "temps-partiel-CDD") {
              return "Temps partiel";
            }
            if (job.contractType === "stage") return "35h/semaine";
            return "35h/semaine";
          };

          // educationRequirements : certification BNSSA ou BPJEPS AAN/BEESAN selon la catégorie.
          const educationCredential = job.category === "BNSSA"
            ? "BNSSA — Brevet National de Sécurité et de Sauvetage Aquatique"
            : "BPJEPS AAN, BEESAN ou Licence STAPS — diplômes habilitant aux fonctions de Maître Nageur Sauveteur";

          // responsibilities : activities pour AE/Intérim, sinon description sous forme courte.
          const responsibilitiesText = isMissionContract && Array.isArray(job.activities) && job.activities.length > 0
            ? job.activities.join(", ")
            : (job.description || "").slice(0, 1000);

          // qualifications : combine requirements + obligation réglementaire.
          const qualificationsText = [
            ...(job.requirements || []),
            job.category === "BNSSA"
              ? "Carte professionnelle à jour, PSE1 valide (recyclage annuel obligatoire)"
              : "Carte professionnelle d'éducateur sportif, CAEPMNS valide (recyclage tous les 5 ans), PSE1 à jour",
          ].filter(Boolean).join(". ");

          // baseSalary : un seul bloc cohérent selon le type de contrat.
          let baseSalary: Record<string, unknown> | undefined;
          if (isInterim && typeof job.missionPrice === "number") {
            baseSalary = {
              "@type": "MonetaryAmount",
              "currency": "EUR",
              "value": {
                "@type": "QuantitativeValue",
                "value": job.missionPrice,
                "unitText": "DAY",
              },
            };
          } else if (isAutoEntrepreneur && job.hourlyRate) {
            // hourlyRate peut être une fourchette (ex. "18-22"). On extrait min/max si possible.
            const rateMatch = job.hourlyRate.match(/(\d+(?:[.,]\d+)?)\s*[-–à]\s*(\d+(?:[.,]\d+)?)/);
            if (rateMatch) {
              baseSalary = {
                "@type": "MonetaryAmount",
                "currency": "EUR",
                "value": {
                  "@type": "QuantitativeValue",
                  "minValue": parseFloat(rateMatch[1].replace(",", ".")),
                  "maxValue": parseFloat(rateMatch[2].replace(",", ".")),
                  "unitText": "HOUR",
                },
              };
            } else {
              baseSalary = {
                "@type": "MonetaryAmount",
                "currency": "EUR",
                "value": {
                  "@type": "QuantitativeValue",
                  "value": job.hourlyRate,
                  "unitText": "HOUR",
                },
              };
            }
          } else if (!isAutoEntrepreneur && !isInterim && job.salary) {
            // Salaire mensuel — extraction d'une fourchette si possible (ex. "1800-2100 €").
            const salMatch = job.salary.match(/(\d{3,5})\s*[-–à]\s*(\d{3,5})/);
            if (salMatch) {
              baseSalary = {
                "@type": "MonetaryAmount",
                "currency": "EUR",
                "value": {
                  "@type": "QuantitativeValue",
                  "minValue": parseInt(salMatch[1], 10),
                  "maxValue": parseInt(salMatch[2], 10),
                  "unitText": "MONTH",
                },
              };
            } else {
              baseSalary = {
                "@type": "MonetaryAmount",
                "currency": "EUR",
                "value": {
                  "@type": "QuantitativeValue",
                  "value": job.salary,
                  "unitText": "MONTH",
                },
              };
            }
          }

          const finalUrl = canonicalUrl || `https://www.bnssa-mns.fr/offre/${job.id}`;

          return {
            "@context": "https://schema.org",
            "@type": "JobPosting",
            "title": job.title,
            "description": htmlDesc,
            "datePosted": job.publishedAt,
            ...(validThroughIso ? { "validThrough": validThroughIso } : {}),
            ...(isMissionContract && job.missionDate ? { "jobStartDate": job.missionDate } : {}),
            ...(!isMissionContract && job.startDate ? { "jobStartDate": job.startDate } : {}),
            ...((job.contractType === "CDD" || job.contractType === "temps-partiel-CDD") && job.endDate ? { "jobEndDate": job.endDate } : {}),
            "employmentType": (
              job.contractType === "CDI" ? "FULL_TIME" :
              job.contractType === "CDD" || job.contractType === "CDD-saisonnier" ? "TEMPORARY" :
              job.contractType === "temps-partiel-CDI" || job.contractType === "temps-partiel-CDD" ? "PART_TIME" :
              job.contractType === "interim" ? "TEMPORARY" :
              job.contractType === "stage" ? "INTERN" :
              job.contractType === "auto-entrepreneur" ? "CONTRACTOR" :
              "OTHER"
            ),
            // identifier : recommandé par Google pour la déduplication des offres.
            "identifier": {
              "@type": "PropertyValue",
              "name": job.employer,
              "value": job.id,
            },
            "hiringOrganization": {
              "@type": "Organization",
              "name": job.employer,
              "sameAs": "https://www.bnssa-mns.fr",
              ...(job.logo ? { "logo": job.logo } : {}),
            },
            "jobLocation": {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": job.location,
                // Correction orthographique : "Ile et Villaine" → "Ille-et-Vilaine"
                "addressRegion": (job.department || "").replace(/Ile\s+et\s+Villaine/gi, "Ille-et-Vilaine"),
                "addressCountry": "FR",
              },
            },
            // applicantLocationRequirements : France métropolitaine + DOM-TOM.
            "applicantLocationRequirements": {
              "@type": "Country",
              "name": "FR",
            },
            ...(baseSalary ? { "baseSalary": baseSalary } : {}),
            // occupationalCategory : combine code ROME/SOC et libellé pour le matching.
            "occupationalCategory": job.category === "BNSSA"
              ? "G1204 Animation de loisirs auprès d'enfants ou d'adolescents — Surveillant de baignade BNSSA"
              : "G1204 Éducation en activités sportives — Maître Nageur Sauveteur",
            "industry": job.category === "BNSSA" ? "Surveillance aquatique" : "Activités aquatiques et natation",
            // workHours : horaires structurés (mission ou cadre hebdomadaire).
            "workHours": computeWorkHours(),
            // educationRequirements : certification professionnelle requise.
            "educationRequirements": {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "professional certification",
              "name": educationCredential,
            },
            // experienceRequirements : 0 mois par défaut (débutants acceptés).
            "experienceRequirements": {
              "@type": "OccupationalExperienceRequirements",
              "monthsOfExperience": 0,
            },
            "qualifications": qualificationsText,
            "responsibilities": responsibilitiesText,
            "skills": (job.requirements || []).join(", "),
            "jobBenefits": (job.benefits || []).join(", "),
            "directApply": true,
            "url": finalUrl,
          };
        })()}
      />

      {/* Fil d'ariane : Accueil > Univers > Offres > Titre de l'annonce */}
      {(() => {
        const breadcrumbItems: BreadcrumbItem[] = isBNSSA
          ? [
              { label: "BNSSA", href: "/bnssa" },
              { label: "Offres d'emploi BNSSA", href: "/bnssa/offres-emploi" },
              { label: job.title },
            ]
          : [
              { label: "Maître Nageur Sauveteur", href: "/maitre-nageur-sauveteur" },
              { label: "Offres d'emploi MNS", href: "/maitre-nageur-sauveteur/offres-emploi" },
              { label: job.title },
            ];
        return (
          <div className="bg-gray-50 border-b border-gray-200">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
              <Breadcrumb items={breadcrumbItems} accent={isBNSSA ? "red" : "blue"} />
            </div>
          </div>
        );
      })()}

      {/* Hero */}
      <section className={`relative ${accentBgLight} border-b ${accentBorder} py-10 ${missionFilled ? "overflow-hidden" : ""}`}>
        {/* Tampon « Mission pourvue » — affiché dans le hero quand la mission est pourvue.
            Purement décoratif (pointer-events-none) pour ne pas gêner l'interaction. */}
        {missionFilled && (
          <div className="pointer-events-none absolute top-4 right-4 sm:top-6 sm:right-8 z-10">
            <span
              className="inline-block uppercase font-extrabold tracking-[0.12em] text-sm sm:text-base px-4 py-2 rounded-md"
              style={{
                color: "#dc2626",
                border: "3px solid #dc2626",
                background: "rgba(255,255,255,0.78)",
                transform: "rotate(-8deg)",
                boxShadow: "0 2px 12px rgba(220,38,38,0.18)",
              }}
            >
              Mission pourvue
            </span>
          </div>
        )}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to={job.category === "BNSSA" ? "/bnssa/offres-emploi" : "/maitre-nageur-sauveteur/offres-emploi"}
            className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour aux offres
          </Link>

          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 ${accentBg} text-white text-xs font-bold rounded-md`}>
              {isBNSSA ? <ShieldCheck className="w-3 h-3" /> : <Waves className="w-3 h-3" />}
              {job.category}
            </span>
            <span className="inline-flex items-center px-2.5 py-1 bg-white border border-gray-300 text-gray-700 text-xs font-semibold rounded-md">
              {CONTRACT_LABELS[job.contractType]}
            </span>
            {job.urgent && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-orange-100 border border-orange-300 text-orange-700 text-xs font-bold rounded-md">
                <Zap className="w-3 h-3" />
                Urgent
              </span>
            )}
            {job.logementPossible && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 border border-emerald-300 text-emerald-700 text-xs font-semibold rounded-md">
                <Home className="w-3 h-3" />
                Logement possible
              </span>
            )}
            {job.horairesFixes && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 border border-gray-300 text-gray-700 text-xs font-semibold rounded-md">
                <Clock className="w-3 h-3" />
                Horaires fixes
              </span>
            )}
            {job.coursParticuliers && job.category === "MNS" && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 border border-blue-300 text-blue-700 text-xs font-semibold rounded-md">
                <GraduationCap className="w-3 h-3" />
                Cours particuliers 100%
              </span>
            )}
            {job.possibiliteCDI && (job.contractType === "CDD" || job.contractType === "temps-partiel-CDD") && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-violet-50 border border-violet-300 text-violet-700 text-xs font-semibold rounded-md">
                <TrendingUp className="w-3 h-3" />
                Possibilité CDI
              </span>
            )}
          </div>

          {job.logo ? (
            <div className="flex items-center gap-4 mb-4">
              <div
                className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white border border-gray-200 overflow-hidden flex items-center justify-center shadow-sm"
                aria-hidden="true"
              >
                <img
                  src={job.logo}
                  alt={`Logo ${job.employer}`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-contain p-1.5"
                  onError={(e) => {
                    (e.currentTarget.parentElement as HTMLElement | null)?.style.setProperty('display', 'none');
                  }}
                />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">{job.title}</h1>
            </div>
          ) : (
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">{job.title}</h1>
          )}

          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-700 mb-4">
            <div className="flex items-center gap-1.5">
              <Building2 className="w-4 h-4 text-gray-500" />
              <span className="font-medium">{job.employer}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <MapPin className="w-4 h-4 text-gray-500" />
              <span>{job.location} — {job.department}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-gray-500" />
              <span>Publié le {new Date(job.publishedAt).toLocaleDateString("fr-FR")}</span>
            </div>
            {job.deadline && (
              <div className="flex items-center gap-1.5 text-amber-700">
                <Clock className="w-4 h-4" />
                <span>Date limite : {new Date(job.deadline).toLocaleDateString("fr-FR")}</span>
              </div>
            )}
            {!isMissionContract && job.startDate && (
              <div className={`flex items-center gap-1.5 ${accentText}`}>
                <Calendar className="w-4 h-4" />
                <span>Prise de poste souhaitée : {new Date(job.startDate).toLocaleDateString("fr-FR")}</span>
              </div>
            )}
            {(job.contractType === "CDD" || job.contractType === "temps-partiel-CDD") && job.endDate && (
              <div className={`flex items-center gap-1.5 ${accentText}`}>
                <Calendar className="w-4 h-4" />
                <span>Date de fin : {new Date(job.endDate).toLocaleDateString("fr-FR")}</span>
              </div>
            )}
          </div>

          {!isAutoEntrepreneur && !isInterim && job.salary && (
            <div className="inline-flex items-center gap-2 bg-white border border-gray-300 px-3 py-1.5 rounded-lg text-sm font-bold text-gray-900">
              <Euro className="w-4 h-4 text-gray-500" />
              {job.salary}
            </div>
          )}
          {isAutoEntrepreneur && job.hourlyRate && (
            <div className="inline-flex items-center gap-2 bg-white border border-gray-300 px-3 py-1.5 rounded-lg text-sm font-bold text-gray-900">
              <Euro className="w-4 h-4 text-gray-500" />
              Fourchette indicative : {job.hourlyRate}
            </div>
          )}
          {isInterim && typeof job.missionPrice === "number" && (
            <div className="inline-flex items-center gap-2 bg-white border border-gray-300 px-3 py-1.5 rounded-lg text-sm font-bold text-gray-900">
              <Euro className="w-4 h-4 text-gray-500" />
              <span>{job.missionPrice} € <span className="font-semibold text-gray-600">/ mission</span></span>
            </div>
          )}

          {/* Bloc Mission unique — mis en évidence pour AE et Intérim */}
          {hasMissionDetails && (
            <div className={`mt-4 inline-flex flex-wrap items-center gap-3 sm:gap-4 px-4 py-3 rounded-xl border-2 ${
              isInterim ? "bg-purple-50 border-purple-200" : "bg-amber-50 border-amber-200"
            }`}>
              <div className="flex items-center gap-2">
                <Calendar className={`w-4 h-4 ${isInterim ? "text-purple-700" : "text-amber-700"}`} />
                <span className="text-sm font-bold text-gray-900">
                  {job.missionDate ? formatMissionDateLong(job.missionDate) : "Mission ponctuelle"}
                </span>
              </div>
              {job.missionStartTime && job.missionEndTime && (
                <div className="flex items-center gap-2">
                  <Clock className={`w-4 h-4 ${isInterim ? "text-purple-700" : "text-amber-700"}`} />
                  <span className="text-sm font-semibold text-gray-800">
                    {job.missionStartTime} – {job.missionEndTime}
                  </span>
                </div>
              )}
              {typeof job.missionHours === "number" && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${
                  isInterim ? "bg-purple-200 text-purple-900" : "bg-amber-200 text-amber-900"
                }`}>
                  {job.missionHours} h
                </span>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Body */}
      <section className="py-12 pb-28 lg:pb-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
            <div className="space-y-8">
              {/* Description */}
              {job.description && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-3">Description du poste</h2>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">{job.description}</p>
                </div>
              )}

              {/* Activités cochées — AE / Intérim uniquement */}
              {isMissionContract && Array.isArray(job.activities) && job.activities.length > 0 && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-3">Activités de la mission</h2>
                  <ul className="flex flex-wrap gap-2">
                    {job.activities.map((act, i) => (
                      <li
                        key={i}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border ${
                          isInterim
                            ? "bg-purple-50 border-purple-200 text-purple-800"
                            : "bg-amber-50 border-amber-200 text-amber-800"
                        }`}
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        {act}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Requirements (masqué pour AE/Intérim ou si vide) */}
              {!isMissionContract && job.requirements && job.requirements.length > 0 && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-3">Profil recherché</h2>
                  <ul className="space-y-2">
                    {job.requirements.map((req, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-700">
                        <CheckCircle2 className={`w-5 h-5 ${accentText} flex-shrink-0 mt-0.5`} />
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Benefits (masqué pour AE/Intérim ou si vide) */}
              {!isMissionContract && job.benefits && job.benefits.length > 0 && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-3">Avantages</h2>
                  <ul className="space-y-2">
                    {job.benefits.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-700">
                        <span className={`w-5 h-5 ${accentBg} text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5`}>+</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Employer info */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                <h2 className="text-lg font-bold text-gray-900 mb-2">À propos de l'employeur</h2>
                <p className="text-gray-700 text-sm">
                  <strong>{job.employer}</strong> — Établissement situé à {job.location}.
                </p>
              </div>
            </div>

            {/* Application Form */}
            <aside id="postuler" className="scroll-mt-24">
              <div className="bg-white border border-gray-200 rounded-xl p-6 sticky top-20 shadow-sm">
                <h2 className="text-lg font-bold text-gray-900 mb-1">Postuler à cette offre</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Inscription gratuite.
                </p>

                {/* Barre de progression — candidature auto-entrepreneur uniquement.
                    Étapes : Profil → Dossier → Tarif → Confirmation.
                    EXTRAITE du formulaire afin d'être visible à TOUTES les étapes
                    (y compris quand le dossier est en attente / refusé / inexistant,
                    cas où le formulaire lui-même n'est pas rendu).
                    Affichée en permanence (même pendant `dossierStatusLoading`) pour
                    éviter le flash visuel : la détection d'étape se base sur des
                    booléens déjà fiables (submitted / aeDossierApproved / isLoggedIn). */}
                {isAutoEntrepreneur && (() => {
                  // Détermination de l'étape courante (1–4)
                  // 1 = Profil   : non connecté
                  // 2 = Dossier  : connecté, dossier en cours / refusé / inexistant
                  // 3 = Tarif    : connecté, dossier validé, tarif à saisir
                  // 4 = Confirm. : candidature envoyée
                  const currentStep =
                    submitted ? 4
                    : aeDossierApproved ? 3
                    : isLoggedIn ? 2
                    : 1;
                  const steps = [
                    { n: 1, label: "Profil", Icon: UserIcon },
                    { n: 2, label: "Diplôme", Icon: FolderCheck },
                    { n: 3, label: "Tarif", Icon: Euro },
                    { n: 4, label: "Confirmation", Icon: CheckCircle2 },
                  ] as const;
                  return (
                    <div
                      className="mb-5"
                      role="progressbar"
                      aria-label="Progression de la candidature auto-entrepreneur"
                      aria-valuemin={1}
                      aria-valuemax={4}
                      aria-valuenow={currentStep}
                      aria-valuetext={`Étape ${currentStep} sur 4 : ${steps[currentStep - 1].label}`}
                    >
                      <ol className="flex items-center justify-between gap-1 relative">
                        {steps.map((s, idx) => {
                          const done = s.n < currentStep;
                          const active = s.n === currentStep;
                          const upcoming = s.n > currentStep;
                          return (
                            <li key={s.n} className="flex-1 flex flex-col items-center relative min-w-0">
                              {idx > 0 && (
                                <span
                                  aria-hidden="true"
                                  className={`absolute top-3.5 h-0.5 ${
                                    done || active ? accentBg : "bg-gray-200"
                                  }`}
                                  style={{ left: "-50%", right: "50%" }}
                                />
                              )}
                              <span
                                className={`relative z-10 w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold border-2 transition-colors ${
                                  done
                                    ? `${accentBg} text-white border-transparent`
                                    : active
                                    ? `bg-white ${accentText} ${accentBorder} ring-2 ring-offset-1 ${
                                        isBNSSA ? "ring-red-400" : "ring-blue-400"
                                      }`
                                    : "bg-white text-gray-400 border-gray-300"
                                }`}
                              >
                                {done ? <CheckCircle2 className="w-4 h-4" /> : <s.Icon className="w-3.5 h-3.5" />}
                              </span>
                              <span
                                className={`mt-1 text-[10px] font-semibold text-center leading-tight ${
                                  active ? accentText : upcoming ? "text-gray-400" : "text-gray-700"
                                }`}
                              >
                                {s.label}
                              </span>
                            </li>
                          );
                        })}
                      </ol>
                      <p className="sr-only">Étape {currentStep} sur 4 : {steps[currentStep - 1].label}</p>
                    </div>
                  );
                })()}

                {missionFilled ? (
                  // Mission pourvue : candidature désactivée (un candidat a déjà été retenu)
                  <div className="py-2">
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                      <span
                        className="inline-block uppercase font-extrabold tracking-[0.12em] text-sm px-4 py-2 rounded-md mb-3"
                        style={{
                          color: "#dc2626",
                          border: "3px solid #dc2626",
                          background: "rgba(255,255,255,0.8)",
                          transform: "rotate(-4deg)",
                        }}
                      >
                        Mission pourvue
                      </span>
                      <p className="text-sm text-gray-700">
                        Cette mission n'est plus disponible : un candidat a déjà été retenu.
                      </p>
                    </div>
                    <button
                      type="button"
                      disabled
                      aria-disabled="true"
                      className="mt-3 w-full bg-gray-300 text-gray-600 py-3 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 cursor-not-allowed"
                    >
                      <Send className="w-4 h-4" />
                      Candidature fermée
                    </button>
                    <Link
                      to="/offres"
                      className="mt-3 block text-center text-sm font-semibold text-blue-600 hover:underline"
                    >
                      Voir les autres offres
                    </Link>
                  </div>
                ) : submitted ? (
                  <div className="text-center py-6">
                    <div className={`w-14 h-14 ${accentBgLight} ${accentText} rounded-full flex items-center justify-center mx-auto mb-3`}>
                      <CheckCircle2 className="w-8 h-8" />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">Candidature envoyée !</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Nous avons bien reçu votre candidature. Le recruteur vous contactera prochainement.
                    </p>
                    <Link
                      to="/offres"
                      className="text-sm font-semibold text-blue-600 hover:underline"
                    >
                      Voir d'autres offres
                    </Link>
                  </div>
                ) : dossierStatusLoading ? (
                  <div className="py-8 text-center text-sm text-gray-500">Chargement…</div>
                ) : showAEBlockedMessage ? (
                  // Candidat AE connecté : dossier non validé (en attente, refusé ou inexistant)
                  <div className="py-4">
                    {dossierStatus === 'rejected' ? (
                      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-bold text-red-800 text-sm mb-1">Dossier refusé</p>
                            <p className="text-xs text-red-700">
                              Votre dossier auto-entrepreneur a été refusé par notre équipe. Merci de le mettre à jour depuis votre <Link to="/dashboard?tab=dossier" className="underline font-semibold">tableau de bord</Link>.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : dossierStatus === 'pending' ? (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <Clock className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-bold text-amber-800 text-sm mb-1">Dossier en cours de validation</p>
                            <p className="text-xs text-amber-700">
                              Merci de patienter que notre équipe valide votre dossier pour cette offre "auto-entrepreneur".
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <FolderCheck className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-bold text-blue-900 text-sm mb-1">Téléversez votre dossier professionnel</p>
                            <p className="text-xs text-blue-800">
                              Pour postuler à une offre auto-entrepreneur, vous devez d'abord téléverser votre CV et toutes les pièces de votre dossier (carte pro, assurance, diplôme, PSE, etc.) depuis votre <Link to="/dashboard?tab=dossier" className="underline font-semibold">tableau de bord</Link>.
                            </p>
                            <p className="text-[11px] font-semibold text-blue-900 mt-2 flex items-start gap-1">
                              <ShieldCheck className="w-3 h-3 mt-0.5 flex-shrink-0 text-green-600" />
                              <span>Uniquement bnssa-mns.fr aura accès à ces documents, aucunement les recruteurs/clients.</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    <Link
                      to="/dashboard?tab=dossier"
                      className={`mt-3 w-full ${accentBg} hover:opacity-90 text-white py-2.5 rounded-lg font-semibold text-sm transition-opacity flex items-center justify-center gap-2`}
                    >
                      <Upload className="w-4 h-4" />
                      Aller à mon dossier
                    </Link>
                    <p className="text-[11px] text-gray-500 mt-3 text-center">
                      Une fois votre dossier validé, vous pourrez postuler à cette offre.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Indicateur discret de sauvegarde automatique du brouillon */}
                    {(draftSavedAt || draftJustSaved) && (
                      <div
                        className={`flex items-center justify-end gap-1 text-[10px] transition-colors ${
                          draftJustSaved ? "text-green-600" : "text-gray-400"
                        }`}
                        aria-live="polite"
                      >
                        <Save className="w-3 h-3" />
                        <span>
                          {draftJustSaved
                            ? "Brouillon sauvegardé"
                            : `Brouillon enregistré à ${draftSavedAt ? new Date(draftSavedAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : ""}`}
                        </span>
                      </div>
                    )}

                    {isLoggedIn && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-xs text-blue-800 flex items-center gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>
                          Connecté(e) en tant que <strong>{prefillEmail}</strong>. Votre identité est récupérée automatiquement.
                        </span>
                      </div>
                    )}
                    <div>
                      <label htmlFor="fullName" className="block text-xs font-semibold text-gray-700 mb-1">
                        Nom complet <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="fullName"
                        type="text"
                        required
                        readOnly={isLoggedIn && Boolean(prefillFullName)}
                        value={form.fullName}
                        onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${
                          isLoggedIn && Boolean(prefillFullName) ? "bg-gray-50 text-gray-700 cursor-not-allowed" : ""
                        }`}
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs font-semibold text-gray-700 mb-1">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="email"
                        type="email"
                        required
                        readOnly={isLoggedIn}
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${
                          isLoggedIn ? "bg-gray-50 text-gray-700 cursor-not-allowed" : ""
                        }`}
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-xs font-semibold text-gray-700 mb-1">
                        Téléphone
                      </label>
                      <input
                        id="phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      />
                    </div>
                    {!isAutoEntrepreneur && (
                      <div>
                        <label htmlFor="cv" className="block text-xs font-semibold text-gray-700 mb-1">
                          CV (PDF)
                        </label>
                        <input
                          id="cv"
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => {
                            const f = e.target.files?.[0] || null;
                            setCvFile(f);
                            setForm({ ...form, cvFileName: f?.name || "" });
                          }}
                          className="w-full text-xs text-gray-600 file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                        />
                      </div>
                    )}

                    {isAutoEntrepreneur && (
                      <div className={`${accentBgLight} ${accentBorder} border rounded-lg p-3 ${aeRateLocked ? "opacity-60" : ""}`}>
                        <label htmlFor="proposedRate" className="block text-xs font-semibold text-gray-800 mb-1">
                          Votre tarif horaire proposé (€/h) <span className="text-red-500">*</span>
                        </label>
                        {aeRateLocked ? (
                          <p className="text-[11px] text-gray-700 mb-2 italic flex items-start gap-1">
                            <Clock className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            <span>Disponible après validation de votre dossier par notre équipe.</span>
                          </p>
                        ) : (
                          <p className="text-[11px] text-gray-600 mb-2">
                            En tant qu'auto-entrepreneur, vous fixez librement votre tarif horaire pour cette mission.
                          </p>
                        )}
                        <div className="relative">
                          <input
                            id="proposedRate"
                            type="number"
                            min="0"
                            step="0.5"
                            required={!aeRateLocked}
                            disabled={aeRateLocked}
                            value={form.proposedRate}
                            onChange={(e) => setForm({ ...form, proposedRate: e.target.value })}
                            className={`w-full pl-3 pr-12 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${
                              aeRateLocked ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-white"
                            }`}
                            placeholder={aeRateLocked ? "Disponible après validation du dossier" : "Ex : 18"}
                            aria-describedby="rate-help"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-500">
                            €/h
                          </span>
                        </div>
                        {job.hourlyRate && !aeRateLocked && (
                          <p id="rate-help" className="text-[11px] text-gray-600 mt-1.5">
                            Fourchette indicative du recruteur : <strong>{job.hourlyRate}</strong>
                          </p>
                        )}
                      </div>
                    )}

                    {/* Dossier professionnel auto-entrepreneur (uniquement post-inscription) */}
                    {isAutoEntrepreneur && !aePreSignup && dossierAlreadyOnFile && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <FolderCheck className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-green-800">
                              Dossier professionnel {storedDossier?.category} déjà enregistré
                            </p>
                            <p className="text-[11px] text-green-700 mt-0.5">
                              Vos pièces (carte pro, assurance, diplômes, PSE…) sont réutilisées automatiquement pour cette candidature.
                            </p>
                            <Link
                              to="/dashboard"
                              className="inline-block text-[11px] font-semibold text-green-800 underline mt-1"
                            >
                              Mettre à jour mes documents
                            </Link>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Bloc d'information : après envoi de la candidature AE, le candidat sera
                        redirigé vers son dashboard pour téléverser ses pièces. Affiché aux
                        candidats AE connectés sans dossier. */}
                    {isAutoEntrepreneur && !aePreSignup && !dossierAlreadyOnFile && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <FolderCheck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-blue-900">
                              Documents du dossier professionnel
                            </p>
                            <p className="text-[11px] text-blue-800 mt-0.5">
                              Après envoi de votre candidature, vous serez redirigé vers votre tableau de bord pour téléverser vos pièces (carte pro, assurance, diplômes, PSE…). Notre équipe validera ensuite votre dossier.
                            </p>
                            <p className="text-[11px] font-semibold text-blue-900 mt-1 flex items-start gap-1">
                              <ShieldCheck className="w-3 h-3 mt-0.5 flex-shrink-0 text-green-600" />
                              <span>Uniquement bnssa-mns.fr aura accès à ces documents, aucunement les recruteurs/clients.</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ANCIEN bloc d'upload des pièces — désactivé.
                        Avec la nouvelle UX, les pièces sont téléversées depuis le dashboard
                        après la création de compte / candidature. */}
                    {!isAutoEntrepreneur && (
                      <div>
                        <label htmlFor="message" className="block text-xs font-semibold text-gray-700 mb-1">
                          Lettre de motivation <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          id="message"
                          required
                          rows={4}
                          value={form.message}
                          onChange={(e) => setForm({ ...form, message: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                          placeholder="Présentez-vous brièvement..."
                        />
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={submitting}
                      className={`w-full ${accentBg} hover:opacity-90 text-white py-3 rounded-lg font-semibold text-sm transition-opacity flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed`}
                    >
                      <Send className="w-4 h-4" />
                      {submitting ? "Envoi en cours…" : "Envoyer ma candidature"}
                    </button>
                    <p className="text-xs text-gray-500 text-center">
                      En postulant, vous acceptez nos{" "}
                      <Link to="/cgv" className="underline">CGU</Link>.
                    </p>
                  </form>
                )}
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Sticky CTA mobile uniquement — Postuler.
          Affichée en permanence sur < lg, masquée sur desktop (où le formulaire est en sidebar sticky).
          Cible l'ancre #postuler pour faire défiler vers le formulaire de candidature.
          État adaptatif : candidature envoyée / AE dossier non validé / candidature normale. */}
      {!submitted && (
        <div
          className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] px-4 py-3 pb-[calc(env(safe-area-inset-bottom)+12px)]"
          role="region"
          aria-label="Postuler à cette offre"
        >
          {missionFilled ? (
            <button
              type="button"
              disabled
              aria-disabled="true"
              className="w-full bg-gray-300 text-gray-600 py-3 rounded-lg font-bold text-sm flex items-center justify-center gap-2 cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
              Mission pourvue
            </button>
          ) : showAEBlockedMessage ? (
            <Link
              to="/dashboard?tab=dossier"
              className={`w-full ${accentBg} hover:opacity-90 text-white py-3 rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition-opacity`}
            >
              <Upload className="w-4 h-4" />
              {dossierStatus === 'pending'
                ? 'Dossier en cours de validation'
                : dossierStatus === 'rejected'
                ? 'Mettre à jour mon dossier'
                : 'Téléverser mon dossier'}
            </Link>
          ) : (
            <a
              href="#postuler"
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById('postuler');
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  // Focus le premier champ après un court délai pour faciliter la saisie
                  setTimeout(() => {
                    const firstInput = el.querySelector<HTMLInputElement>('input, textarea');
                    firstInput?.focus({ preventScroll: true });
                  }, 500);
                }
              }}
              className={`w-full ${accentBg} hover:opacity-90 text-white py-3 rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition-opacity`}
            >
              <Send className="w-4 h-4" />
              Postuler à cette offre
            </a>
          )}
        </div>
      )}

      {/* Modale d'inscription rapide pour candidat non connecté */}
      <CandidateSignupModal
        isOpen={signupOpen}
        email={form.email}
        fullName={form.fullName}
        phone={form.phone || undefined}
        onClose={() => setSignupOpen(false)}
        onSuccess={async (newUserId) => {
          // Pour les offres auto-entrepreneur :
          //  Une fois le compte créé (code de sécurité validé), on NE soumet PAS
          //  la candidature. Le candidat est redirigé directement vers son tableau
          //  de bord pour téléverser son CV et toutes les pièces de son dossier.
          //  Il pourra ensuite postuler une fois le dossier validé par l'admin.
          if (isAutoEntrepreneur) {
            setSignupOpen(false);
            toast.success("Compte créé ! Téléversez maintenant vos documents pour faire valider votre dossier.");
            setTimeout(() => navigate("/dashboard?tab=dossier"), 800);
            return;
          }
          // Standard : on enregistre la candidature avec le vrai userId
          const ok = await submitApplication(newUserId, newUserId);
          if (ok) {
            setSignupOpen(false);
          }
        }}
      />
    </Layout>
  );
}
